/**
 * Notification Center Page
 *
 * Provides role-specific notification views:
 * - Admin: Pending feedback approvals + missing group feedback reminders
 * - Teacher: Missing group feedback reminders for their course groups
 * - Parent: Aggregated approved feedback for their children
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import {
  getAdminNotifications,
  getTeacherNotifications,
  getParentNotifications
} from '../../api/notificationPortalApi';
import {
  approveFeedback,
  rejectFeedback,
  updateAndApproveFeedback
} from '../../api/feedbackPortalApi';

// Styled components
const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 24px;
  color: #1f2937;
`;

const RoleBadge = styled.span`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.$color || '#e0e7ff'};
  color: ${props => props.$textColor || '#3730a3'};
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  h2 {
    margin: 0;
    font-size: 18px;
    color: #374151;
  }
`;

const CountBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  background: ${props => props.$bg || '#fee2e2'};
  color: ${props => props.$color || '#dc2626'};
`;

const NotificationCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
`;

const BadgeGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => props.$bg || '#dbeafe'};
  color: ${props => props.$color || '#1e40af'};
`;

const TypeBadge = styled(Badge)`
  background: ${props => props.$type === 'group' ? '#dbeafe' : '#ede9fe'};
  color: ${props => props.$type === 'group' ? '#1e40af' : '#6b21a8'};
`;

const StatusBadge = styled(Badge)`
  background: ${props => {
    switch (props.$status) {
      case 'pending': return '#fef3c7';
      case 'approved': return '#dcfce7';
      case 'rejected': return '#fee2e2';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'pending': return '#92400e';
      case 'approved': return '#166534';
      case 'rejected': return '#dc2626';
      default: return '#6b7280';
    }
  }};
`;

const WeekBadge = styled(Badge)`
  background: #dbeafe;
  color: #1e40af;
`;

const CourseGroupInfo = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const MetaInfo = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 8px;
`;

const ContentDisplay = styled.div`
  background: #f9fafb;
  border-radius: 6px;
  padding: 12px;
  font-size: 14px;
  color: #1f2937;
  line-height: 1.5;
  white-space: pre-wrap;
  margin-bottom: 12px;
`;

const ContentTextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
  margin-bottom: 12px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ApproveButton = styled(Button)`
  background: #dcfce7;
  color: #166534;

  &:hover:not(:disabled) {
    background: #bbf7d0;
  }
`;

const RejectButton = styled(Button)`
  background: #fef3c7;
  color: #92400e;

  &:hover:not(:disabled) {
    background: #fde68a;
  }
`;

const EditButton = styled(Button)`
  background: #dbeafe;
  color: #1e40af;

  &:hover:not(:disabled) {
    background: #bfdbfe;
  }
`;

const CancelButton = styled(Button)`
  background: #f3f4f6;
  color: #6b7280;

  &:hover:not(:disabled) {
    background: #e5e7eb;
  }
`;

const SaveButton = styled(Button)`
  background: #3b82f6;
  color: white;

  &:hover:not(:disabled) {
    background: #2563eb;
  }
`;

const MissingWeeksList = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const MissingWeekBadge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: #fee2e2;
  color: #dc2626;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 48px;
  color: #dc2626;
  background: #fee2e2;
  border-radius: 8px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: #6b7280;
  background: #f9fafb;
  border-radius: 8px;
`;

const ChildSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ChildHeader = styled.div`
  background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
  padding: 12px 16px;
  border-radius: 8px 8px 0 0;
  color: white;
  font-weight: 600;
  font-size: 15px;
`;

const ChildFeedbackList = styled.div`
  border: 1px solid #e5e7eb;
  border-top: none;
  border-radius: 0 0 8px 8px;
  padding: 12px;
`;

// Role Tabs for multi-role users
const RoleTabContainer = styled.div`
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 24px;
  background: white;
  border-radius: 8px 8px 0 0;
  overflow: hidden;
`;

const RoleTab = styled.button`
  padding: 14px 24px;
  border: none;
  background: ${props => props.$active ? '#F1AF59' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#6b7280'};
  font-size: 14px;
  font-weight: ${props => props.$active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${props => props.$active ? '#F1AF59' : '#f3f4f6'};
    color: ${props => props.$active ? 'white' : '#374151'};
  }
`;

const RoleTabBadge = styled.span`
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.$active ? 'rgba(255,255,255,0.3)' : '#e5e7eb'};
  color: ${props => props.$active ? 'white' : '#6b7280'};
`;

// Reject Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-radius: 12px 12px 0 0;

  h3 {
    margin: 0;
    font-size: 16px;
    color: white;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: white;
  line-height: 1;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }
`;

const ModalContent = styled.div`
  padding: 20px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
`;

const NotificationCenter = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Multi-role data states
  const [adminData, setAdminData] = useState(null);
  const [teacherData, setTeacherData] = useState(null);
  const [parentData, setParentData] = useState(null);
  const [activeRoleTab, setActiveRoleTab] = useState(null);

  // Inline editing state
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');

  // Reject modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingFeedback, setRejectingFeedback] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Get user roles
  const userGroups = auth.user?.profile['cognito:groups'] || [];
  const isAdmin = userGroups.some(g =>
    ['portal_developer', 'portal_system_admin', 'portal_director', 'portal_manager', 'portal_admin'].includes(g)
  );
  const isTeacher = userGroups.includes('portal_teacher');
  const isParent = userGroups.includes('portal_parent');

  // Build user roles array for tabs
  const getUserRoles = () => {
    const roles = [];
    if (isAdmin) roles.push({ key: 'admin', label: 'Admin', priority: 1 });
    if (isTeacher) roles.push({ key: 'teacher', label: 'Teacher', priority: 2 });
    if (isParent) roles.push({ key: 'parent', label: 'Parent', priority: 3 });
    return roles.sort((a, b) => a.priority - b.priority);
  };

  const userRoles = getUserRoles();
  const hasMultipleRoles = userRoles.length > 1;

  // Get notification count for each role
  const getNotificationCount = (roleKey) => {
    switch (roleKey) {
      case 'admin':
        return (adminData?.pendingFeedback?.length || 0) + (adminData?.missingFeedback?.length || 0);
      case 'teacher':
        return teacherData?.missingFeedback?.length || 0;
      case 'parent':
        return parentData?.children?.reduce((sum, child) => sum + child.feedback.length, 0) || 0;
      default:
        return 0;
    }
  };

  // Determine display role for header badge
  const getDisplayRole = () => {
    if (userGroups.includes('portal_system_admin')) return { name: 'System Admin', color: '#fee2e2', textColor: '#dc2626' };
    if (userGroups.includes('portal_director')) return { name: 'Director', color: '#fef3c7', textColor: '#92400e' };
    if (userGroups.includes('portal_manager')) return { name: 'Manager', color: '#dcfce7', textColor: '#166534' };
    if (userGroups.includes('portal_admin')) return { name: 'Admin', color: '#dbeafe', textColor: '#1e40af' };
    if (userGroups.includes('portal_teacher')) return { name: 'Teacher', color: '#ede9fe', textColor: '#6b21a8' };
    if (userGroups.includes('portal_parent')) return { name: 'Parent', color: '#e0e7ff', textColor: '#3730a3' };
    return { name: 'Unknown', color: '#f3f4f6', textColor: '#6b7280' };
  };

  const displayRole = getDisplayRole();

  // Fetch notifications for all user roles in parallel
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const fetchPromises = [];

      if (isAdmin) {
        fetchPromises.push(
          getAdminNotifications()
            .then(result => setAdminData(result))
            .catch(err => console.error('Error fetching admin notifications:', err))
        );
      }

      if (isTeacher) {
        fetchPromises.push(
          getTeacherNotifications()
            .then(result => setTeacherData(result))
            .catch(err => console.error('Error fetching teacher notifications:', err))
        );
      }

      if (isParent) {
        fetchPromises.push(
          getParentNotifications()
            .then(result => setParentData(result))
            .catch(err => console.error('Error fetching parent notifications:', err))
        );
      }

      if (fetchPromises.length === 0) {
        setError('No valid role found for notifications');
      } else {
        await Promise.all(fetchPromises);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isTeacher, isParent]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Set default active tab after data is loaded
  useEffect(() => {
    if (!loading && userRoles.length > 0 && !activeRoleTab) {
      setActiveRoleTab(userRoles[0].key);
    }
  }, [loading, userRoles, activeRoleTab]);

  // Remove a handled feedback from the local pending list. Approving or
  // rejecting an individual feedback never changes the missing-feedback
  // section (that only tracks group feedback existence), so a local update
  // is enough - no need to refetch the whole notification payload.
  const removePendingFeedback = (feedbackId) => {
    setAdminData(prev => prev ? {
      ...prev,
      pendingFeedback: (prev.pendingFeedback || []).filter(fb => fb.id !== feedbackId)
    } : prev);
  };

  // Handle approve
  const handleApprove = async (feedbackId) => {
    try {
      setActionLoading(feedbackId);
      await approveFeedback(feedbackId);
      removePendingFeedback(feedbackId);
    } catch (err) {
      console.error('Error approving feedback:', err);
      alert('Failed to approve: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle reject
  const handleOpenRejectModal = (feedback) => {
    setRejectingFeedback(feedback);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert('Please enter a rejection reason');
      return;
    }

    try {
      setActionLoading(rejectingFeedback.id);
      await rejectFeedback(rejectingFeedback.id, rejectReason.trim());
      removePendingFeedback(rejectingFeedback.id);
      setRejectModalOpen(false);
      setRejectingFeedback(null);
      setRejectReason('');
    } catch (err) {
      console.error('Error rejecting feedback:', err);
      alert('Failed to reject: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle inline edit
  const handleStartEdit = (feedback) => {
    setEditingId(feedback.id);
    setEditContent(feedback.content || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditContent('');
  };

  const handleSaveAndApprove = async (feedbackId) => {
    try {
      setActionLoading(feedbackId);
      await updateAndApproveFeedback(feedbackId, editContent.trim());
      removePendingFeedback(feedbackId);
      setEditingId(null);
      setEditContent('');
    } catch (err) {
      console.error('Error updating and approving:', err);
      alert('Failed to save and approve: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Navigate to course group
  const handleNavigateToCourseGroup = (courseGroupId) => {
    navigate('/portal/my-course-groups', { state: { selectedGroupId: courseGroupId } });
  };

  // Render Admin Notifications
  const renderAdminNotifications = () => {
    const { pendingFeedback = [], missingFeedback = [] } = adminData || {};

    return (
      <>
        {/* Pending Approvals Section */}
        <Section>
          <SectionHeader>
            <h2>Pending Approvals</h2>
            <CountBadge $bg="#fef3c7" $color="#92400e">
              {pendingFeedback.length}
            </CountBadge>
          </SectionHeader>

          {pendingFeedback.length === 0 ? (
            <EmptyState>No pending feedback to approve</EmptyState>
          ) : (
            pendingFeedback.map(feedback => (
              <NotificationCard key={feedback.id}>
                <CardHeader>
                  <BadgeGroup>
                    <TypeBadge $type={feedback.type}>
                      {feedback.type === 'group' ? 'Group' : 'Individual'}
                    </TypeBadge>
                    <StatusBadge $status="pending">Pending</StatusBadge>
                    <WeekBadge>Week {feedback.week}</WeekBadge>
                  </BadgeGroup>
                  <CourseGroupInfo>
                    {feedback.courseGroupName} - {feedback.campusName}
                  </CourseGroupInfo>
                </CardHeader>

                <MetaInfo>
                  Teacher: {feedback.teacherName}
                  {feedback.studentName && <> | Student: {feedback.studentName}</>}
                  {' | '}
                  {new Date(feedback.createdAt).toLocaleDateString()}
                </MetaInfo>

                {editingId === feedback.id ? (
                  <ContentTextArea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    placeholder="Edit feedback content..."
                  />
                ) : (
                  <ContentDisplay>{feedback.content}</ContentDisplay>
                )}

                <ActionButtons>
                  {editingId === feedback.id ? (
                    <>
                      <SaveButton
                        onClick={() => handleSaveAndApprove(feedback.id)}
                        disabled={actionLoading === feedback.id}
                      >
                        {actionLoading === feedback.id ? 'Saving...' : 'Save & Approve'}
                      </SaveButton>
                      <CancelButton
                        onClick={handleCancelEdit}
                        disabled={actionLoading === feedback.id}
                      >
                        Cancel
                      </CancelButton>
                    </>
                  ) : (
                    <>
                      <ApproveButton
                        onClick={() => handleApprove(feedback.id)}
                        disabled={actionLoading === feedback.id}
                      >
                        {actionLoading === feedback.id ? 'Approving...' : 'Approve'}
                      </ApproveButton>
                      <RejectButton
                        onClick={() => handleOpenRejectModal(feedback)}
                        disabled={actionLoading === feedback.id}
                      >
                        Reject
                      </RejectButton>
                      <EditButton
                        onClick={() => handleStartEdit(feedback)}
                        disabled={actionLoading === feedback.id}
                      >
                        Edit & Approve
                      </EditButton>
                    </>
                  )}
                </ActionButtons>
              </NotificationCard>
            ))
          )}
        </Section>

        {/* Missing Feedback Section */}
        <Section>
          <SectionHeader>
            <h2>Missing Group Feedback</h2>
            <CountBadge>
              {missingFeedback.length}
            </CountBadge>
          </SectionHeader>

          {missingFeedback.length === 0 ? (
            <EmptyState>All course groups have recent feedback</EmptyState>
          ) : (
            missingFeedback.map(item => (
              <NotificationCard key={item.courseGroupId}>
                <CourseGroupInfo>
                  {item.courseGroupName}
                </CourseGroupInfo>
                <MetaInfo>
                  {item.campusName}
                  {item.scheduleDays && <> | {item.scheduleDays}</>}
                  {item.teacherNames && item.teacherNames.length > 0 && (
                    <> | Teacher: {item.teacherNames.join(', ')}</>
                  )}
                  {' | '}Current Week: {item.currentWeek}
                </MetaInfo>
                <MissingWeeksList>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>Missing:</span>
                  {item.missingWeeks.map(week => (
                    <MissingWeekBadge key={week}>Week {week}</MissingWeekBadge>
                  ))}
                </MissingWeeksList>
              </NotificationCard>
            ))
          )}
        </Section>
      </>
    );
  };

  // Render Teacher Notifications
  const renderTeacherNotifications = () => {
    const { missingFeedback = [] } = teacherData || {};

    return (
      <Section>
        <SectionHeader>
          <h2>Missing Group Feedback</h2>
          <CountBadge>
            {missingFeedback.length}
          </CountBadge>
        </SectionHeader>

        {missingFeedback.length === 0 ? (
          <EmptyState>All your course groups have recent feedback</EmptyState>
        ) : (
          missingFeedback.map(item => (
            <NotificationCard
              key={item.courseGroupId}
              style={{ cursor: 'pointer' }}
              onClick={() => handleNavigateToCourseGroup(item.courseGroupId)}
            >
              <CourseGroupInfo>
                {item.courseGroupName}
              </CourseGroupInfo>
              <MetaInfo>
                {item.campusName}
                {item.scheduleDays && <> | {item.scheduleDays}</>}
                {item.teacherNames && item.teacherNames.length > 0 && (
                  <> | Teacher: {item.teacherNames.join(', ')}</>
                )}
                {' | '}Current Week: {item.currentWeek}
              </MetaInfo>
              <MissingWeeksList>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>Missing:</span>
                {item.missingWeeks.map(week => (
                  <MissingWeekBadge key={week}>Week {week}</MissingWeekBadge>
                ))}
              </MissingWeeksList>
              <MetaInfo style={{ marginTop: '8px', color: '#3b82f6' }}>
                Click to create feedback
              </MetaInfo>
            </NotificationCard>
          ))
        )}
      </Section>
    );
  };

  // Render Parent Notifications
  const renderParentNotifications = () => {
    const { children = [] } = parentData || {};

    return (
      <Section>
        <SectionHeader>
          <h2>Feedback for Your Children</h2>
          <CountBadge $bg="#dcfce7" $color="#166534">
            {children.reduce((sum, child) => sum + child.feedback.length, 0)}
          </CountBadge>
        </SectionHeader>

        {children.length === 0 ? (
          <EmptyState>No feedback available yet</EmptyState>
        ) : (
          children.map(child => (
            <ChildSection key={child.childId}>
              <ChildHeader>{child.childName}</ChildHeader>
              <ChildFeedbackList>
                {child.feedback.length === 0 ? (
                  <EmptyState>No feedback for this child yet</EmptyState>
                ) : (
                  child.feedback.map(fb => (
                    <NotificationCard key={fb.id} style={{ marginBottom: '8px' }}>
                      <CardHeader>
                        <BadgeGroup>
                          <TypeBadge $type={fb.type}>
                            {fb.type === 'group' ? 'Group' : 'Individual'}
                          </TypeBadge>
                          <WeekBadge>Week {fb.week}</WeekBadge>
                        </BadgeGroup>
                        <CourseGroupInfo>
                          {fb.courseGroupName}
                        </CourseGroupInfo>
                      </CardHeader>
                      <MetaInfo>
                        {fb.campusName} | Teacher: {fb.teacherName}
                        {' | '}
                        {new Date(fb.createdAt).toLocaleDateString()}
                      </MetaInfo>
                      <ContentDisplay>{fb.content}</ContentDisplay>
                    </NotificationCard>
                  ))
                )}
              </ChildFeedbackList>
            </ChildSection>
          ))
        )}
      </Section>
    );
  };

  return (
    <Container>
      <Header>
        <Title>Notification Center</Title>
        <RoleBadge $color={displayRole.color} $textColor={displayRole.textColor}>
          {displayRole.name}
        </RoleBadge>
      </Header>

      {loading && <LoadingState>Loading notifications...</LoadingState>}

      {error && <ErrorState>Error: {error}</ErrorState>}

      {!loading && !error && (
        <>
          {/* Role Tabs - only show if user has multiple roles */}
          {hasMultipleRoles && (
            <RoleTabContainer>
              {userRoles.map(role => (
                <RoleTab
                  key={role.key}
                  $active={activeRoleTab === role.key}
                  onClick={() => setActiveRoleTab(role.key)}
                >
                  {role.label}
                  <RoleTabBadge $active={activeRoleTab === role.key}>
                    {getNotificationCount(role.key)}
                  </RoleTabBadge>
                </RoleTab>
              ))}
            </RoleTabContainer>
          )}

          {/* Content based on active tab (or single role) */}
          {(activeRoleTab === 'admin' || (!hasMultipleRoles && isAdmin)) && adminData && (
            renderAdminNotifications()
          )}
          {(activeRoleTab === 'teacher' || (!hasMultipleRoles && isTeacher)) && teacherData && (
            renderTeacherNotifications()
          )}
          {(activeRoleTab === 'parent' || (!hasMultipleRoles && isParent)) && parentData && (
            renderParentNotifications()
          )}
        </>
      )}

      {/* Reject Modal */}
      {rejectModalOpen && (
        <ModalOverlay>
          <Modal>
            <ModalHeader>
              <h3>Reject Feedback</h3>
              <CloseButton onClick={() => setRejectModalOpen(false)}>&times;</CloseButton>
            </ModalHeader>
            <ModalContent>
              <p style={{ marginBottom: '12px', color: '#6b7280' }}>
                Please provide a reason for rejecting this feedback:
              </p>
              <ContentTextArea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason..."
                autoFocus
              />
            </ModalContent>
            <ModalFooter>
              <CancelButton onClick={() => setRejectModalOpen(false)}>
                Cancel
              </CancelButton>
              <RejectButton
                onClick={handleReject}
                disabled={actionLoading === rejectingFeedback?.id}
              >
                {actionLoading === rejectingFeedback?.id ? 'Rejecting...' : 'Reject'}
              </RejectButton>
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default NotificationCenter;
